import React, { useEffect, useRef, useState } from 'react';
import {
  formatCurrencyValueForInput,
  parseCurrencyText,
  getEditingString
} from '../../utils/currencyInput';

const MoneyInput = ({
  value,
  onCommit,
  className = '',
  formatDisplay = formatCurrencyValueForInput,
  commitEmptyAs = '',
  allowNegative = false,
  selectOnFocus = true,
  onFocus,
  onBlur,
  onClick,
  inputRef: forwardedRef,
  ...rest
}) => {
  const internalRef = useRef(null);
  const inputRef = forwardedRef || internalRef;
  const [editingValue, setEditingValue] = useState(formatCurrencyValueForInput(value));
  const [isFocused, setIsFocused] = useState(false);
  const hasSelectedOnFocus = useRef(false);
  const lastCommittedRef = useRef(value);

  useEffect(() => {
    if (!isFocused) {
      setEditingValue(formatDisplay(value));
      hasSelectedOnFocus.current = false;
    }
    lastCommittedRef.current = value;
  }, [value, isFocused, formatDisplay]);

  const commitValue = (rawValue) => {
    if (!onCommit) return;

    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      onCommit(commitEmptyAs);
      return;
    }

    const parsed = parseCurrencyText(rawValue, { allowNegative });
    const normalized = parsed === '' ? commitEmptyAs : parseFloat(parsed.toFixed(2));
    onCommit(normalized);
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    setEditingValue(getEditingString(value));

    if (selectOnFocus && !hasSelectedOnFocus.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.select();
          hasSelectedOnFocus.current = true;
        }
      });
    }

    if (onFocus) onFocus(event);
  };

  const handleClick = (event) => {
    if (onClick) onClick(event);

    if (!selectOnFocus) return;

    if (document.activeElement === inputRef.current && !hasSelectedOnFocus.current) {
      inputRef.current.select();
      hasSelectedOnFocus.current = true;
    }
  };

  const handleChange = (event) => {
    const sanitized = event.target.value
      .replace(allowNegative ? /[^0-9.,-]/g : /[^0-9.,]/g, '')
      .replace(',', '.');

    const [whole, ...decimals] = sanitized.split('.');
    const normalizedInput = decimals.length > 0 ? `${whole}.${decimals.join('')}` : whole;

    setEditingValue(normalizedInput);
  };

  const handleBlur = (event) => {
    commitValue(editingValue);
    setIsFocused(false);
    hasSelectedOnFocus.current = false;
    if (onBlur) onBlur(event);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      commitValue(editingValue);
      requestAnimationFrame(() => inputRef.current?.blur());
    } else if (event.key === 'Escape') {
      const resetValue = getEditingString(lastCommittedRef.current);
      setEditingValue(resetValue);
      commitValue(resetValue);
      requestAnimationFrame(() => inputRef.current?.blur());
    }
  };

  const displayValue = isFocused ? editingValue : formatDisplay(value);

  return (
    <input
      {...rest}
      ref={inputRef}
      type="text"
      className={className}
      value={displayValue}
      inputMode="decimal"
      onFocus={handleFocus}
      onClick={handleClick}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

export default MoneyInput;
